<template>
  <div>
    <b-card>
      <breadcrumbs
        :breadcrumb-items="breadcrumbItems"
      />
      <PartnerToolbar
        v-if="Object.keys(partner).length !== 0"
        :partner="partner"
      />
    </b-card>
    <StatsNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-bottom">
            <div class="head-card-top mb-2">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-phoenix' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-phoenix', params: { id: $route.params.id } }"
                pill
                exact
              >
                {{ $t('Overview') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-phoenix-partners' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-phoenix-partners', params: { id: $route.params.id } }"
                pill
                exact
              >
                {{ $t('DSP Partners') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-phoenix-geo' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-phoenix-geo', params: { id: $route.params.id } }"
                pill
                exact
              >
                {{ $t('Geo') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-phoenix-device' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-phoenix-device', params: { id: $route.params.id } }"
                pill
                exact
              >
                {{ $t('Device') }}
              </b-button>
            </div>
          </div>
          <div class="head-card-top">
            <h4>
              {{ $t('Phoenix DSP Partner Statistics') }}:
              <router-link
                :to="{ name: 'dashboard-dsppartner', params: { id: partnerId }}"
                class="text-primary"
              >
                {{ partnerName }}
              </router-link>
            </h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              v-model="rangePicker"
              :is-loading="loading"
              @range-change="toggleRange"
              @refresh="refreshData"
            />
          </div>
        </div>
      </div>
      <div
        v-if="loading"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner label="Loading..." />
      </div>
      <div v-else>
        <b-table
          small
          :items="statsV2Data"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          responsive
          striped
          foot-clone
        >
          <template #head(date)>
            {{ $t('Date') }}
          </template>

          <template #head(impressions)>
            {{ $t('message.tableHeader.impressions') }}
          </template>

          <template #head(clicks)>
            {{ $t('message.tableHeader.clicks') }}
          </template>

          <template #head(revenue)>
            {{ $t('message.tableHeader.ecpm') }}
          </template>

          <template #cell(date)="data">
            {{ data.value ? getFormattedDate(new Date(data.value)) : '' }}
          </template>

          <template #cell(impressions)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>

          <template #cell(clicks)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>

          <template #cell(revenue)="data">
            {{ currency('USD', data.value) }}
          </template>

          <template #foot(date)="data">
            <strong :data-label="data.label">{{ $t('Records') }}: {{ statsV2Data.length }}</strong>
          </template>

          <template #foot(impressions)="data">
            <strong :data-label="data.label">{{ totalFormat('impressions').toLocaleString('Ru-ru') }}</strong>
          </template>

          <template #foot(clicks)="data">
            <strong :data-label="data.label">{{ totalFormat('clicks').toLocaleString('Ru-ru') }}</strong>
          </template>

          <template #foot(revenue)="data">
            <strong :data-label="data.label">{{ currency('USD', totalFormat('revenue')) }}</strong>
          </template>
        </b-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import { getUserData } from '@/auth/utils'
import useJwt from '@/auth/jwt/useJwt'
import Ripple from 'vue-ripple-directive'
import {
  BCard,
  BTable,
  BSpinner,
  VBTooltip,
  BButton,
} from 'bootstrap-vue'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import PartnerToolbar from '../PartnerToolbar.vue'
import StatsNavigation from './StatsNavigation.vue'
import StatsDateRangeFilter from './StatsDateRangeFilter.vue'

export default {
  name: 'StatsPhoenixPartnersDetails',
  components: {
    Breadcrumbs,
    BCard,
    BTable,
    BSpinner,
    PartnerToolbar,
    StatsNavigation,
    StatsDateRangeFilter,
    BButton,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      loading: true,
      pageTitle: '',
      pageSubtitle: '',
      breadcrumbItems: [],
      partner: {},
      partnerId: null,
      partnerName: '',
      fields: [
        { key: 'date', label: 'Date', tdClass: 'td-width' },
        { key: 'impressions', label: 'Impressions', tdClass: 'td-width' },
        { key: 'clicks', label: 'Clicks', tdClass: 'td-width' },
        { key: 'revenue', label: 'Revenue', tdClass: 'td-width' },
      ],
      sortBy: 'date',
      sortDesc: true,
      sortDirection: 'desc',
      authUserData: getUserData(),
      currency: currencyFormatter,
      getFormattedDate,
      statsV2Data: [],
      rangePicker: [
        getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        getFormattedDate(new Date()),
      ],
      rangeDefault: [
        getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        getFormattedDate(new Date()),
      ],
    }
  },

  async created() {
    await this.getPartnerData()
  },
  methods: {
    async getPartnerData() {
      const responseData = await useJwt.getPartner(this.$route.params.id)
      this.partner = responseData.data.partner || {}
      this.partnerId = this.$route.params.partnerId
      this.getPartnerName()
      this.setBreabcrumbs()
      this.getStatsV2Data()
    },
    async getPartnerName() {
      try {
        const responseData = await useJwt.dsppartners()
        const partnerData = responseData.data.dsppartners.find(p => p._id === this.partnerId)
        if (partnerData) {
          this.partnerName = partnerData.name || `Partner ${this.partnerId}`
        } else {
          this.partnerName = `Partner ${this.partnerId}`
        }
      } catch (error) {
        // Error fetching partner name
        this.partnerName = `Partner ${this.partnerId}`
      }
    },
    async getStatsV2Data() {
      if (!this.partnerId) {
        return
      }

      this.loading = true
      this.statsV2Data = []

      if (!this.rangePicker || this.rangePicker.length < 2) {
        this.loading = false
        return
      }

      const params = {
        partner_id: this.partnerId,
        openrtb_partner_id: this.$route.params.id,
        from: this.rangePicker[0],
        to: this.rangePicker[1],
        type: 'daily',
        limit: 80,
      }
      const responseData = await useJwt.getPhoenixDspPartner(params)
      this.statsV2Data = responseData.data.items || []
      this.loading = false
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('RTB partners'),
          to: '/partners/',
        },
        {
          text: this.partner.name,
          to: `/partners/${this.partner._id}/`,
        },
        {
          text: this.$t('Phoenix DSP Partners Statistics'),
          to: `/partners/${this.partner._id}/stats/phoenix-partners`,
        },
        {
          text: this.partnerName,
          active: true,
        },
      ]
    },

    totalFormat(field) {
      let total = 0
      this.statsV2Data.forEach(i => {
        if (field === 'date') return // Skip date field for totals
        total += i[field] || 0
      })
      return total
    },
    toggleRange(e) {
      this.rangePicker = e
      this.getStatsV2Data()
      this.$router.replace({
        name: this.$route.name,
        query: { from: e[0], to: e[1] },
      })
    },
    refreshData() {
      this.getStatsV2Data()
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
<style>
  .col-picker {
    min-width: 220px;
  }
</style>
